// finds and returns the first element of the array having a pkey property equal to the given pkey. 
export function findInArrayByPkey(arr, pkey) {
    if (Array.isArray(arr)) return arr.find(el => { return (el.pkey === pkey); });
    return null;
}

export function arrayContainsObjectWithPkey(arr, pkey) {
    if (Array.isArray(arr)) return arr.some(el => { return (el.pkey === pkey); });
    return false;
}

// removes in-place all elements from the array having a pkey property equal to the given pkey 
export function removeFromArrayByPkey(arr, pkey) {
    if (arr?.length > 0) {
        let i = 0;
        while (i < arr.length) {
            if (arr[i]?.pkey === pkey) {
                arr.splice(i, 1);
            } else {
                ++i;
            }
        }
    }
    return arr;
}

export function handleErrorReponseWithToast(error, toast)
{
    if (error.response === undefined) return;

    if (error.response.status == 401) return;

    if (error.response.data !== undefined && error.response.data.error !== undefined) {
        toast.add({severity: "error", summary: "Request failed", detail: error.response.data.error, life: 15000 });
    }
    else {
        toast.add({severity: "error",  summary: "Request failed",
                    detail: "An error occurred. Server responded with status " + error.response.status, life: 15000 });
    }
}

export function getStringFromErrorResponse(error)
{
    if (error.response === undefined) {
        return error?.message ?? "Generic error";
    }

    if (error.response?.status == 401) {
        return "Unauthorized";
    }

    let sServerErrorText = error.response?.data?.error ?? "";
    if (error.response?.data?.error_descr)
    {
        sServerErrorText += ". "+error.response?.data?.error_descr;
    }

    if (error.response?.data?.error !== undefined) {
        return sServerErrorText;
    }
    else {
        return "An error occurred. Server responded with status " + error.response?.status;
    }
}

export function processErrorResponseWithToast(error, store)
{
    if (error.response === undefined) {
        store.commit("seterror", {severity: "error", caption : "Generic error", message: error?.message ?? "", duration: 10000} );
        return;
    }

    if (error.response?.status == 401) {
        return; // ignore 401 Unauthorized
    }

    let sServerErrorText = error.response?.data?.error ?? "";
    if (error.response?.data?.error_descr)
    {
        sServerErrorText += ". "+error.response?.data?.error_descr;
    }

    if (error.response?.data?.error !== undefined) {
        store.commit("seterror", {severity: "error", caption: "Request failed", 
                    message: sServerErrorText, duration: 15000} );
    }
    else {
        store.commit("seterror", {severity: "error", caption: "Request failed", 
                    message: "An error occurred. Server responded with status " + error.response?.status, duration: 15000} );
    }
}

export function processUploadErrorEventWithToast(event, store) {
  let statusCode = event?.xhr?.status ?? 0;
  let errInfo = "Status " + statusCode;
  let responseErrorInfo = event.xhr?.statusText ?? "";
  if (responseErrorInfo.length > 0) {
    errInfo += ", " + responseErrorInfo + ". ";
  }

  try {
    let err = JSON.parse(event.xhr?.responseText ?? "{}");
    responseErrorInfo = err?.error ?? "";
  } catch (e) {
    responseErrorInfo = "";
  }

  if (responseErrorInfo.length > 0) {
    errInfo += responseErrorInfo;
  }

  store.commit("seterror", {severity: "error", caption: "Upload failed", message: "Failed to upload file. "+errInfo, duration: 10000} );
}

export function formatSecondsToString(elapsedSeconds) {
    let s = elapsedSeconds;
    let min = elapsedSeconds / 60;
    let h = min / 60;

    if (min > 0) {
        s -= (min*60);
    }

    if (h > 0) {
        min -= (h*60);
    }

    if (h > 0) {
        return h.toFixed(2)+"h";
    } else if (min > 0) {
        return min.toFixed(2)+"min";
    } else {
        return s + "s";
    }
}

const today = new Date();
let yesterday = new Date();
yesterday.setDate(yesterday.getDate() - 1);
const dateoptionsSameMonth = { weekday: 'short', month: 'long', day: '2-digit' };
const dateoptionsSameYear = { month: 'long', day: '2-digit' };
const dateoptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
const timeoptions = { timeStyle: 'short' };


const isSameDay = (d1, d2) => {
    return d1.getDate() == d2.getDate() && d1.getMonth() == d2.getMonth() && d1.getFullYear() == d2.getFullYear();
}

const isSameMonth = (d1, d2) => {
    return d1.getMonth() == d2.getMonth() && d1.getFullYear() == d2.getFullYear();
}

const isSameYear = (d1, d2) => {
    return d1.getFullYear() == d2.getFullYear();
}

// Converts the ISO timestamp string to a javascript Date object or returns today on failure
export function timestampStringToDate(tstamp, bReturnNullOnFailure = false, bUTCTimestamp = true)
{
    if (tstamp === undefined || tstamp === null) {
        return (bReturnNullOnFailure ? null : today);
    }

    tstamp = tstamp.trim();
    if (tstamp?.length > 0) {
        if (bUTCTimestamp && !tstamp.endsWith('Z')) { // if not UTC
            if (tstamp.indexOf('+', 1) <= 0) { // if no explicit offset is included in the string after index 0
                tstamp += "Z"; // make sure the timestamp contains the UTC marker
            }
        }

        let t = new Date(tstamp);
        // if the date is not we are probably dealing with safari
        if (isNaN(t)) {
            t = new Date(tstamp.replace(/-/g, "/")); // safari doesn't like dashes in dates
        }

        if (isNaN(t)) {
            return (bReturnNullOnFailure ? null : today);
        }
        return t;
    } else {
        return (bReturnNullOnFailure ? null : today);
    }
}

export function timestampToString(tstamp, bVom=false)
{
    if (tstamp.length > 0)
    {
        let t = new Date(tstamp);
        // if the date is not we are probably dealing with safari
        if (isNaN(t)) {
            t = new Date(tstamp.replace(/-/g, "/")); // safari doesn't like dashes in dates
        }
        // TODO - consider using Date.parse

        if (isSameDay(t, today)) {
            return (bVom ? "von " : "") + "heute um " + t.toLocaleTimeString("de-DE", timeoptions);
        } else if (isSameDay(t, yesterday)) {
            return (bVom ? "von " : "") + "gestern um " + t.toLocaleTimeString("de-DE", timeoptions);
        } else {
            if (isSameMonth(t, today))
            {
                return (bVom ? "vom " : "am ") + t.toLocaleDateString("de-DE", dateoptionsSameMonth) + " um " + t.toLocaleTimeString("de-DE", timeoptions);
            } else if (isSameYear(t, today))
            {
                return (bVom ? "vom " : "am ")  + t.toLocaleDateString("de-DE", dateoptionsSameYear) + " um " + t.toLocaleTimeString("de-DE", timeoptions);
            }
            return (bVom ? "vom " : "am ") + t.toLocaleDateString("de-DE", dateoptions) + " um " + t.toLocaleTimeString("de-DE", timeoptions);
        }
    }
    return "";
}

export function randString(length) {
    let result = '';
    let characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let charactersLength = characters.length;
    for (let i = 0; i < length; ++i)
    {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
}

let g_RandomRobotNames = ["Micro","Machina","Eve the Angel","RAM","Badass Cyborg","EVA","Silver Head","Metallic Soul","Automata","Superior Cognizant Simulator","Trek","Olympus","Technician","Quick Responder","Screwie","Axle Head","Army","DeathStar","Helen","Bite","Link","Jet","Gollum","Daemon","Prima","Jupiter","Curiosity","Tallie","Amerigo","UFO","Scrap","Sparkles","Radion","Bruno","Bolt Head","Sputnik","Ash","Ruin","Omon","Percy","Humanoid","Android 99","Villian","Dance Bot","Reflector","Cowbot","Brainy","Snap","Elixir","Knight Rider","Boron","Bolt Head","Antenna","Mari","Chip","UNO","Hector","Achilles","Logan","Levi","Naru","Nora","Steel","Links","Alpha","Alexandra","Longbottom","Robo","Coco","Gem","Nova","Robocop","Slicer","Idiosychonous","Tendrites","Animagus","Siri","Oreo","Wired","Chip Master","Harley","Bolt","Knives","Mercy","Chitta","Spud","Weebio","Halie","Flydroid","Robottle","Rusty","Cybot","Taco","Heavy Metal","Top Head","Goblet of Wires","Raspberry Pie","Fish and Chips","Anne Droid","Megabyte","Cyborgan","Robottoms","AstroBoy","Airbender","Space Nomad","Chappie","Garth","Johnny","Marcus","Kronos","Omega","Mona","BoyBot","Ultron","Cherry","Piper","Simon","Hydra","Sterling","Self Emulator","Opium","Exon","Pixels","Dragon","Arm","Intel","Zeek","Titan","Alexa","Duster","Sully","Alicia","Cassandra","Pearl Mine","Roxy","Molly","Lester","Angela","Alice","Tina","Kitty","Julia","Humana","Freya","Emily","Helena","Purple"];
let g_StarNames = ["Adhil", "Alamak", "Almach", "Alpheratz", "Buna", "Mirach", "Nembus", "Sterrennacht", "Titawin", "Veritate", "Macondo", "Karaka", "Albali", "Ancha", "Bosona", "Bunda", "Lionrock", "Márohu", "Sadachbia", "Sadalmelik", "Sadalsuud", "Situla", "Skat", "Alshain", "Altair", "Chechia", "Libertas", "Okab", "Petra", "Phoenicia", "Tarazed", "Cervantes", "Inquill", "Bharani", "Botein", "Hamal", "Lilii Borea", "Mesarthim", "Sheratan", "Almaaz", "Capella", "Haedus", "Hassaleh", "Lucilinburhuc", "Mahasim", "Menkalinan", "Nervia", "Saclateni", "Tevel", "Alkalurops", "Arcalís", "Arcturus", "Asellus Primus", "Asellus Secundus", "Asellus Tertius", "Izar", "Merga", "Muphrid", "Nekkar", "Nikawiy", "Seginus", "Xuange", "Mago", "Tonatiuh", "Acubens", "Asellus Australis", "Asellus Borealis", "Copernicus", "Gakyid", "Meleph", "Nahn", "Piautos", "Tarf", "Tegmine", "Chara", "Cor Caroli", "La Superba", "Tuiren", "Adhara", "Aludra", "Amadioha", "Atakoraka", "Furud", "Mirzam", "Muliphein", "Sirius", "Unurgunite", "Wezen", "Gomeisa", "Procyon", "Algedi", "Alshat", "Dabih", "Deneb Algedi", "Nashira", "Aspidiske", "Avior", "Canopus", "Miaplacidus", "Tapecue", "Achird", "Caph", "Castula", "Fulu", "Marfak", "Navi", "Nushagak", "Ruchbah", "Schedar", "Segin", "Dofida", "Hadar", "Menkent", "Nyamien", "Proxima Centauri", "Rigil Kentaurus", "Toliman", "Uklun", "Alderamin", "Alfirk", "Al Kalb al Rai", "Errai", "Garnet Star", "Kurhah", "Axólotl", "Baten Kaitos", "Diphda", "Felixvarela", "Kaffaljidhma", "Menkar", "Mira", "Mpingo", "Ceibo", "Elkurud", "Phact", "Wazn", "Diadem", "Meridiana", "Alphecca", "Kamuy", "Moldoveanu", "Nusakan", "Alchiba", "Algorab", "Gienah", "Kraz", "Alkes", "Amansinaya", "Hunahpú", "Acrux", "Gacrux", "Ginan", "Imai", "Mimosa", "Tupã", "Albireo", "Aljanah", "Azelfafage", "Deneb", "Fawaris", "Sadr", "Aldulfin", "Moriah", "Musica", "Rotanev", "Sualocin", "Naledi", "Aldhibah", "Alrakis", "Alruba", "Alsafi", "Altais", "Athebyne", "Dziban", "Edasich", "Eltanin", "Fafnir", "Funi", "Giausar", "Grumium", "Kuma", "Rastaban", "Taiyi", "Thuban", "Tianyi", "Kitalpha", "Acamar", "Achernar", "Angetenar", "Ayeyarwady", "Azha", "Beemim", "Beid", "Chaophraya", "Cursa", "Keid", "Koeia", "Montuno", "Mouhoun", "Ran", "Sceptrum", "Theemin", "Tojil", "Zaurak", "Zibal", "Dalim", "Diya", "Intan", "Alhena", "Alzirr", "Castor", "Jishui", "Mebsuta", "Mekbuda", "Pollux", "Propus", "Tejat", "Wasat", "Aldhanab", "Alnair", "Itonda", "Tiaki", "Cujam", "Franz", "Hunor", "Irena", "Kornephoros", "Maasym", "Marsic", "Ogma", "Pipoltr", "Rasalgethi", "Sarin", "Alphard", "Ashlesha", "Felis", "Lerna", "Minchir", "Ukdah", "Zhang", "Taika", "Adhafera", "Algieba", "Al Minliar al Asad", "Alterf", "Chertan", "Denebola", "Dingolay", "Formosa", "Rasalas", "Regulus", "Sagarmatha", "Shama", "Subra", "Zosma", "Illyrian", "Praecipua", "Arneb", "Nihal", "Brachium", "Zubenelgenubi", "Zubenelhakrabi", "Zubeneschamali", "Kakkab", "Kekouan", "Absolutno", "Alsciaukat", "Gloas", "Koit", "Stribor", "Aladfar", "Alathfar", "Chason", "Sheliak", "Sulafat", "Vega", "Xihe", "Bubup", "Citalá", "Lusitânia", "Polaris Australis", "Barnard's Star", "Cebalrai", "Guniibuu", "Mahsati", "Marfik", "Rasalhague", "Rosalíadecastro", "Sabik", "Timir", "Yed Posterior", "Yed Prior", "Alnilam", "Alnitak", "Bellatrix", "Betelgeuse", "Hatysa", "Meissa", "Mintaka", "Rigel", "Saiph", "Tabit", "Thabit", "Peacock", "Algenib", "Alkarab", "Anadolu", "Biham", "Enif", "Helvetios", "Homam", "Markab", "Matar", "Morava", "Sadalbari", "Salm", "Scheat", "Solaris", "Tangra", "Algol", "Atik", "Berehynia", "Menkib", "Miram", "Mirfak", "Misam", "Muspelheim", "Ankaa", "Nenque", "Wurren", "Alpherg", "Alrescha", "Bélénos", "Citadelle", "Ebla", "Fumalsamakah", "Parumleo", "Revati", "Torcular", "Fomalhaut", "Sāmaya", "Azmidi", "Naos", "Nosaxa", "Tislit", "Tureis", "Tupi", "Sansuna", "Sham", "Uruk", "Ainalrami", "Albaldah", "Alnasl", "Arkab Posterior", "Arkab Prior", "Ascella", "Belel", "Gumala", "Kaus Australis", "Kaus Borealis", "Kaus Media", "Nash", "Nunki", "Pincoya", "Polis", "Rukbat", "Sika", "Terebellum", "Acrab", "Alniyat", "Antares", "Dìwö", "Dschubba", "Fang", "Fuyue", "Graffias", "Iklil", "Jabbah", "Larawag", "Lesath", "Paikauhale", "Pipirima", "Rapeto", "Sargas", "Sharjah", "Shaula", "Xamidimura", "Cocibolca", "Alasia", "Alya", "Gudja", "Kaveh", "Unukalhai", "Bibhā", "Ain", "Alcyone", "Aldebaran", "Asterope, Sterope", "Atlas", "Celaeno", "Chamukuy", "Electra", "Elnath", "Hoggar", "Maia", "Merope", "Pleione", "Prima Hyadum", "Secunda Hyadum", "Taygeta", "Tianguan", "Horna", "Mothallah", "Atria", "Emiw", "Poerava", "Alcor", "Alioth", "Alkaid", "Alkaphrah", "Alula Australis", "Alula Borealis", "Aniara", "Chalawan", "Dombay", "Dubhe", "Intercrus", "Liesma", "Mazaalai", "Megrez", "Merak", "Mizar", "Muscida", "Násti", "Phecda", "Sarir", "Taiyangshou", "Talitha", "Tania Australis", "Tania Borealis", "Baekdu", "Kochab", "Pherkad", "Polaris", "Yildun", "Alsephina", "Kalausi", "Markeb", "Natasha", "Regor", "Suhail", "Elgafar", "Flegetonte", "Heze", "Kang", "Khambalia", "Lich", "Malmok", "Minelauva", "Mönch", "Porrima", "Spica", "Syrma", "Vindemiatrix", "Zaniah", "Zavijava", "Anser"];
let g_ConstellationNames = ['Andromeda', 'Antlia', 'Apus', 'Aquarius', 'Aquila', 'Ara', 'Aries', 'Auriga', 'Boötes', 'Camelopardalis', 'Cancer', 'Canes Venatici', 'Canis Major', 'Canis Minor', 'Capricornus', 'Carina', 'Cassiopeia', 'Centaurus', 'Cepheus', 'Cetus', 'Chamaeleon', 'Columba', 'Coma Berenices', 'Corona Australis', 'Corona Borealis', 'Corvus', 'Crater', 'Crux', 'Cygnus', 'Delphinus', 'Dorado', 'Draco', 'Equuleus', 'Eridanus', 'Fornax', 'Gemini', 'Grus', 'Hercules', 'Hydra', 'Lacerta', 'Leo', 'Leo Minor', 'Lepus', 'Libra', 'Lupus', 'Lynx', 'Lyra', 'Mensa', 'Monoceros', 'Octans', 'Ophiuchus', 'Orion', 'Pavo', 'Pegasus', 'Perseus', 'Phoenix', 'Pisces', 'Piscis Austrinus', 'Puppis', 'Reticulum', 'Sagitta', 'Sagittarius', 'Scorpius', 'Sculptor', 'Serpens', 'Sextans', 'Taurus', 'Triangulum', 'Triangulum Australe', 'Tucana', 'Ursa Major', 'Ursa Minor', 'Vela', 'Virgo', 'Vulpecula']; 
let g_GroupNameIdeas = ['Testers', 'Dev Devices', 'First Adopters', 'The Board Guys', 'The Nerd Herd', 'Mavericks', 'Tech Turtles', 'Back Benchers', 'The Squad', 'Hustlers', 'Unstoppables', 'Homies', 'Madhouse', 'Bro Gang', 'Amazing Pals', 'Lucky Charms', 'The Top Business', 'Hawk Insights', 'Rising Stars', 'Team Passion', 'Ours', 'We Can Do It', 'Best Friends Club', 'Create Choas', 'The Outlaws', 'Corporate Life', 'Happy Hour', 'Being Productive', 'The Minions', 'Unfired', 'No Spamming', 'Smile Please', 'Crazy world', 'Coffee lovers', 'Strong Ties', 'Just talk', 'Weekend Kings', 'Wandering Minds', 'We Are Hulks', 'Kingslayers XII', 'The Drifters', 'Rock & Roll', 'We Talk A Lot', 'Hike & Bike', 'Deals over Meals', 'First Steps', 'Grind House', 'Dream Team', 'Pen Pals', 'Great Mates', 'Fabulousness', 'Walky Talky', 'Fraandship', 'Atomic Reactors', 'Gangnam Style', 'Full On Go Getters', 'The Unknowns', 'Full On', 'Chat Lounge', 'My People', 'Housefull', 'PUBG Pros', 'The Pro Bros', 'The Webslingers', 'Cape Crusaders', 'The Meme Team', 'The Chamber of Secrets', 'The Alter Egos', 'Area 51', 'Memers', 'Jokers', 'Online Hangover', 'Till Eternity', 'Slayers', 'The Swaggers', 'Fearless and Flawless', 'Marketing Magic', 'North Mavericks', 'Remarkable Falcons', 'Tech Geeks', 'Dynamic Drillers', 'Marketing Marauders', 'Super Sellers', 'Risky Businesses', 'The Whiz Kids', 'Future Billionaires', 'Mind Readers', 'Freinds League', 'Kings & Queens', 'My Folks', 'Let’s Party Guys', 'Oh Hello Bros', 'Game Changers', 'Our Superstars', 'Near ones', 'The Invincibles', 'Wazzup', 'The Talk Lounge', 'Justice League', 'Non-stop pings', 'Adventures in texting', 'The Chatters', 'Selfie Squad', 'The Talk-A-Lots', 'No Name Squad', 'The Chamber of Secrets', 'Enter at your own risk'];

export function getRandomRobotName() {
    let idx = Math.floor(Math.random()*g_RandomRobotNames.length);
    if (idx < 0) idx = 0;
    else if (idx >= g_RandomRobotNames.length) idx = g_RandomRobotNames.length-1;
    return g_RandomRobotNames[idx];
}

export function getRandomStarName() {
    let idx = Math.floor(Math.random()*g_StarNames.length);
    if (idx < 0) idx = 0;
    else if (idx >= g_StarNames.length) idx = g_StarNames.length-1;
    return g_StarNames[idx];
}

export function getRandomConstellationName() {
    let idx = Math.floor(Math.random()*g_ConstellationNames.length);
    if (idx < 0) idx = 0;
    else if (idx >= g_ConstellationNames.length) idx = g_ConstellationNames.length-1;
    return g_ConstellationNames[idx];
}

export function getRandomGroupName() {
    let idx = Math.floor(Math.random()*g_GroupNameIdeas.length);
    if (idx < 0) idx = 0;
    else if (idx >= g_GroupNameIdeas.length) idx = g_GroupNameIdeas.length-1;
    return g_GroupNameIdeas[idx];
}

import { ref } from "vue";


export function useCountriesTable()
{
    const countries = ref([
        {code:"AF", name:"Afghanistan"},
        {code:"AX", name:"Åland"},
        {code:"AL", name:"Albanien"},
        {code:"DZ", name:"Algerien"},
        {code:"AS", name:"Amerikanisch-Samoa"},
        {code:"AD", name:"Andorra"},
        {code:"AO", name:"Angola"},
        {code:"AI", name:"Anguilla"},
        {code:"AQ", name:"Antarktis"},
        {code:"AG", name:"Antigua und Barbuda"},
        {code:"AR", name:"Argentinien"},
        {code:"AM", name:"Armenien"},
        {code:"AW", name:"Aruba"},
        {code:"AU", name:"Australien"},
        {code:"AT", name:"Österreich"},
        {code:"AZ", name:"Aserbaidschan"},
        {code:"BS", name:"Bahamas"},
        {code:"BH", name:"Bahrain"},
        {code:"BD", name:"Bangladesch"},
        {code:"BB", name:"Barbados"},
        {code:"BY", name:"Weißrussland"},
        {code:"BE", name:"Belgien"},
        {code:"BZ", name:"Belize"},
        {code:"BJ", name:"Benin"},
        {code:"BM", name:"Bermuda"},
        {code:"BT", name:"Bhutan"},
        {code:"BO", name:"Bolivien"},
        {code:"BQ", name:"Bonaire, Sint Eustatius und Saba"},
        {code:"BA", name:"Bosnien und Herzegowina"},
        {code:"BW", name:"Botswana"},
        {code:"BV", name:"Bouvetinsel"},
        {code:"BR", name:"Brasilien"},
        {code:"IO", name:"Britisches Territorium im Indischen Ozean"},
        {code:"VG", name:"Britische Jungferninseln"},
        {code:"BN", name:"Brunei"},
        {code:"BG", name:"Bulgarien"},
        {code:"BF", name:"Burkina Faso"},
        {code:"BI", name:"Burundi"},
        {code:"KH", name:"Kambodscha"},
        {code:"CM", name:"Kamerun"},
        {code:"CA", name:"Kanada"},
        {code:"CV", name:"Kap Verde"},
        {code:"KY", name:"Kaimaninseln"},
        {code:"CF", name:"Zentralafrikanische Republik"},
        {code:"TD", name:"Tschad"},
        {code:"CL", name:"Chile"},
        {code:"CN", name:"China"},
        {code:"CX", name:"Weihnachtsinsel"},
        {code:"CC", name:"Kokosinseln"},
        {code:"CO", name:"Kolumbien"},
        {code:"KM", name:"Komoren"},
        {code:"CK", name:"Cookinseln"},
        {code:"CR", name:"Costa Rica"},
        {code:"HR", name:"Kroatien"},
        {code:"CU", name:"Kuba"},
        {code:"CW", name:"Curacao"},
        {code:"CY", name:"Zypern"},
        {code:"CZ", name:"Tschechische Republik"},
        {code:"CD", name:"Demokratische Republik Kongo"},
        {code:"DK", name:"Dänemark"},
        {code:"DJ", name:"Dschibuti"},
        {code:"DM", name:"Dominica"},
        {code:"DO", name:"Dominikanische Republik"},
        {code:"TL", name:"Osttimor"},
        {code:"EC", name:"Ecuador"},
        {code:"EG", name:"Ägypten"},
        {code:"SV", name:"El Salvador"},
        {code:"GQ", name:"Äquatorialguinea"},
        {code:"ER", name:"Eritrea"},
        {code:"EE", name:"Estland"},
        {code:"ET", name:"Äthiopien"},
        {code:"FK", name:"Falkland-Inseln"},
        {code:"FO", name:"Färöer-Inseln"},
        {code:"FJ", name:"Fidschi"},
        {code:"FI", name:"Finnland"},
        {code:"FR", name:"Frankreich"},
        {code:"GF", name:"Französisch-Guayana"},
        {code:"PF", name:"Französisch-Polynesien"},
        {code:"TF", name:"Französische Süd- und Antarktisgebiete"},
        {code:"GA", name:"Gabun"},
        {code:"GM", name:"Gambia"},
        {code:"GE", name:"Georgien"},
        {code:"DE", name:"Deutschland"},
        {code:"GH", name:"Ghana"},
        {code:"GI", name:"Gibraltar"},
        {code:"GR", name:"Griechenland"},
        {code:"GL", name:"Grönland"},
        {code:"GD", name:"Grenada"},
        {code:"GP", name:"Guadeloupe"},
        {code:"GU", name:"Guam"},
        {code:"GT", name:"Guatemala"},
        {code:"GG", name:"Guernsey"},
        {code:"GN", name:"Guinea"},
        {code:"GW", name:"Guinea-Bissau"},
        {code:"GY", name:"Guyana"},
        {code:"HT", name:"Haiti"},
        {code:"HM", name:"Heard und McDonaldinseln"},
        {code:"HN", name:"Honduras"},
        {code:"HK", name:"Hongkong"},
        {code:"HU", name:"Ungarn"},
        {code:"IS", name:"Island"},
        {code:"IN", name:"Indien"},
        {code:"ID", name:"Indonesien"},
        {code:"IR", name:"Iran"},
        {code:"IQ", name:"Irak"},
        {code:"IE", name:"Irland"},
        {code:"IM", name:"Isle of Man"},
        {code:"IL", name:"Israel"},
        {code:"IT", name:"Italien"},
        {code:"CI", name:"Elfenbeinküste"},
        {code:"JM", name:"Jamaika"},
        {code:"JP", name:"Japan"},
        {code:"JE", name:"Jersey"},
        {code:"JO", name:"Jordanien"},
        {code:"KZ", name:"Kasachstan"},
        {code:"KE", name:"Kenia"},
        {code:"KI", name:"Kiribati"},
        {code:"XK", name:"Kosovo"},
        {code:"KW", name:"Kuwait"},
        {code:"KG", name:"Kirgisistan"},
        {code:"LA", name:"Laos"},
        {code:"LV", name:"Lettland"},
        {code:"LB", name:"Libanon"},
        {code:"LS", name:"Lesotho"},
        {code:"LR", name:"Liberia"},
        {code:"LY", name:"Libyen"},
        {code:"LI", name:"Liechtenstein"},
        {code:"LT", name:"Litauen"},
        {code:"LU", name:"Luxemburg"},
        {code:"MO", name:"Macao"},
        {code:"MK", name:"Mazedonien"},
        {code:"MG", name:"Madagaskar"},
        {code:"MW", name:"Malawi"},
        {code:"MY", name:"Malaysia"},
        {code:"MV", name:"Malediven"},
        {code:"ML", name:"Mali"},
        {code:"MT", name:"Malta"},
        {code:"MH", name:"Marshallinseln"},
        {code:"MQ", name:"Martinique"},
        {code:"MR", name:"Mauretanien"},
        {code:"MU", name:"Mauritius"},
        {code:"YT", name:"Mayotte"},
        {code:"MX", name:"Mexiko"},
        {code:"FM", name:"Mikronesien"},
        {code:"MD", name:"Moldawien"},
        {code:"MC", name:"Monaco"},
        {code:"MN", name:"Mongolei"},
        {code:"ME", name:"Montenegro"},
        {code:"MS", name:"Montserrat"},
        {code:"MA", name:"Marokko"},
        {code:"MZ", name:"Mosambik"},
        {code:"MM", name:"Myanmar"},
        {code:"NA", name:"Namibia"},
        {code:"NR", name:"Nauru"},
        {code:"NP", name:"Nepal"},
        {code:"NL", name:"Niederlande"},
        {code:"AN", name:"Niederländische Antillen"},
        {code:"NC", name:"Neukaledonien"},
        {code:"NZ", name:"Neuseeland"},
        {code:"NI", name:"Nicaragua"},
        {code:"NE", name:"Niger"},
        {code:"NG", name:"Nigeria"},
        {code:"NU", name:"Niue"},
        {code:"NF", name:"Norfolkinsel"},
        {code:"KP", name:"Nordkorea"},
        {code:"MP", name:"Nördliche Marianen"},
        {code:"NO", name:"Norwegen"},
        {code:"OM", name:"Oman"},
        {code:"PK", name:"Pakistan"},
        {code:"PW", name:"Palau"},
        {code:"PS", name:"Palästinensische Autonomiegebiete"},
        {code:"PA", name:"Panama"},
        {code:"PG", name:"Papua-Neuguinea"},
        {code:"PY", name:"Paraguay"},
        {code:"PE", name:"Peru"},
        {code:"PH", name:"Philippinen"},
        {code:"PN", name:"Pitcairninseln"},
        {code:"PL", name:"Polen"},
        {code:"PT", name:"Portugal"},
        {code:"PR", name:"Puerto Rico"},
        {code:"QA", name:"Katar"},
        {code:"CG", name:"Republik Kongo"},
        {code:"RE", name:"Réunion"},
        {code:"RO", name:"Rumänien"},
        {code:"RU", name:"Russland"},
        {code:"RW", name:"Ruanda"},
        {code:"BL", name:"Saint-Barthélemy"},
        {code:"SH", name:"St. Helena"},
        {code:"KN", name:"St. Kitts und Nevis"},
        {code:"LC", name:"St. Lucia"},
        {code:"MF", name:"St. Martin"},
        {code:"PM", name:"Saint-Pierre und Miquelon"},
        {code:"VC", name:"St. Vincent und die Grenadinen"},
        {code:"WS", name:"Samoa"},
        {code:"SM", name:"San Marino"},
        {code:"ST", name:"São Tomé und Príncipe"},
        {code:"SA", name:"Saudi-Arabien"},
        {code:"SN", name:"Senegal"},
        {code:"RS", name:"Serbien"},
        {code:"CS", name:"Serbien und Montenegro"},
        {code:"SC", name:"Seychellen"},
        {code:"SL", name:"Sierra Leone"},
        {code:"SG", name:"Singapur"},
        {code:"SX", name:"Sint Maarten"},
        {code:"SK", name:"Slowakei"},
        {code:"SI", name:"Slowenien"},
        {code:"SB", name:"Salomon-Inseln"},
        {code:"SO", name:"Somalia"},
        {code:"ZA", name:"Südafrika"},
        {code:"GS", name:"Südgeorgien und die Südlichen Sandwichinseln"},
        {code:"KR", name:"Südkorea"},
        {code:"SS", name:"Südsudan"},
        {code:"ES", name:"Spanien"},
        {code:"LK", name:"Sri Lanka"},
        {code:"SD", name:"Sudan"},
        {code:"SR", name:"Suriname"},
        {code:"SJ", name:"Svalbard und Jan Mayen"},
        {code:"SZ", name:"Swasiland"},
        {code:"SE", name:"Schweden"},
        {code:"CH", name:"Schweiz"},
        {code:"SY", name:"Syrien"},
        {code:"TW", name:"Taiwan"},
        {code:"TJ", name:"Tadschikistan"},
        {code:"TZ", name:"Tansania"},
        {code:"TH", name:"Thailand"},
        {code:"TG", name:"Togo"},
        {code:"TK", name:"Tokelau"},
        {code:"TO", name:"Tonga"},
        {code:"TT", name:"Trinidad und Tobago"},
        {code:"TN", name:"Tunesien"},
        {code:"TR", name:"Türkei"},
        {code:"TM", name:"Turkmenistan"},
        {code:"TC", name:"Turks- und Caicosinseln"},
        {code:"TV", name:"Tuvalu"},
        {code:"VI", name:"Amerikanische Jungferninseln"},
        {code:"UG", name:"Uganda"},
        {code:"UA", name:"Ukraine"},
        {code:"AE", name:"Vereinte Arabische Emirate"},
        {code:"GB", name:"Großbritannien"},
        {code:"US", name:"Vereinigte Staaten von Amerika (USA)"},
        {code:"UM", name:"United States Minor Outlying Islands"},
        {code:"UY", name:"Uruguay"},
        {code:"UZ", name:"Usbekistan"},
        {code:"VU", name:"Vanuatu"},
        {code:"VA", name:"Vatikanstadt"},
        {code:"VE", name:"Venezuela"},
        {code:"VN", name:"Vietnam"},
        {code:"WF", name:"Wallis und Futuna"},
        {code:"EH", name:"Westsahara"},
        {code:"YE", name:"Jemen"},
        {code:"ZM", name:"Sambia"},
        {code:"ZW", name:"Simbabwe"}
    ]);
    return countries;
}

export function useLangCodes() {
    const langcodes = ref([
        {code:"en", name:"English"},
        {code:"de", name:"German"},
        {code:"es", name:"Spanish"},
        {code:"en-AU", name:"English (Australia)"},
        {code:"en-BZ", name:"English (Belize)"},
        {code:"en-CA", name:"English (Canada)"},
        {code:"en-CB", name:"English (Caribbean)"},
        {code:"en-GB", name:"English (United Kingdom)"},
        {code:"en-IE", name:"English (Ireland)"},
        {code:"en-JM", name:"English (Jamaica)"},
        {code:"en-NZ", name:"English (New Zealand)"},
        {code:"en-PH", name:"English (Republic of the Philippines)"},
        {code:"en-TT", name:"English (Trinidad and Tobago)"},
        {code:"en-US", name:"English (United States)"},
        {code:"en-ZA", name:"English (South Africa)"},
        {code:"en-ZW", name:"English (Zimbabwe)"},
        {code:"de-AT", name:"German (Austria)"},
        {code:"de-CH", name:"German (Switzerland)"},
        {code:"de-DE", name:"German (Germany)"},
        {code:"de-LI", name:"German (Liechtenstein)"},
        {code:"de-LU", name:"German (Luxembourg)"},
        {code:"af", name:"Afrikaans"},
        {code:"af-ZA", name:"Afrikaans (South Africa)"},
        {code:"ar", name:"Arabic"},
        {code:"ar-AE", name:"Arabic (U.A.E.)"},
        {code:"ar-BH", name:"Arabic (Bahrain)"},
        {code:"ar-DZ", name:"Arabic (Algeria)"},
        {code:"ar-EG", name:"Arabic (Egypt)"},
        {code:"ar-IQ", name:"Arabic (Iraq)"},
        {code:"ar-JO", name:"Arabic (Jordan)"},
        {code:"ar-KW", name:"Arabic (Kuwait)"},
        {code:"ar-LB", name:"Arabic (Lebanon)"},
        {code:"ar-LY", name:"Arabic (Libya)"},
        {code:"ar-MA", name:"Arabic (Morocco)"},
        {code:"ar-OM", name:"Arabic (Oman)"},
        {code:"ar-QA", name:"Arabic (Qatar)"},
        {code:"ar-SA", name:"Arabic (Saudi Arabia)"},
        {code:"ar-SY", name:"Arabic (Syria)"},
        {code:"ar-TN", name:"Arabic (Tunisia)"},
        {code:"ar-YE", name:"Arabic (Yemen)"},
        {code:"az", name:"Azeri (Latin)"},
        {code:"az-AZ", name:"Azeri (Latin) (Azerbaijan)"},
        {code:"az-AZ", name:"Azeri (Cyrillic) (Azerbaijan)"},
        {code:"be", name:"Belarusian"},
        {code:"be-BY", name:"Belarusian (Belarus)"},
        {code:"bg", name:"Bulgarian"},
        {code:"bg-BG", name:"Bulgarian (Bulgaria)"},
        {code:"bs-BA", name:"Bosnian (Bosnia and Herzegovina)"},
        {code:"ca", name:"Catalan"},
        {code:"ca-ES", name:"Catalan (Spain)"},
        {code:"cs", name:"Czech"},
        {code:"cs-CZ", name:"Czech (Czech Republic)"},
        {code:"cy", name:"Welsh"},
        {code:"cy-GB", name:"Welsh (United Kingdom)"},
        {code:"da", name:"Danish"},
        {code:"da-DK", name:"Danish (Denmark)"},
        {code:"dv", name:"Divehi"},
        {code:"dv-MV", name:"Divehi (Maldives)"},
        {code:"el", name:"Greek"},
        {code:"el-GR", name:"Greek (Greece)"},
        {code:"eo", name:"Esperanto"},
        {code:"es-AR", name:"Spanish (Argentina)"},
        {code:"es-BO", name:"Spanish (Bolivia)"},
        {code:"es-CL", name:"Spanish (Chile)"},
        {code:"es-CO", name:"Spanish (Colombia)"},
        {code:"es-CR", name:"Spanish (Costa Rica)"},
        {code:"es-DO", name:"Spanish (Dominican Republic)"},
        {code:"es-EC", name:"Spanish (Ecuador)"},
        {code:"es-ES", name:"Spanish (Castilian)"},
        {code:"es-ES", name:"Spanish (Spain)"},
        {code:"es-GT", name:"Spanish (Guatemala)"},
        {code:"es-HN", name:"Spanish (Honduras)"},
        {code:"es-MX", name:"Spanish (Mexico)"},
        {code:"es-NI", name:"Spanish (Nicaragua)"},
        {code:"es-PA", name:"Spanish (Panama)"},
        {code:"es-PE", name:"Spanish (Peru)"},
        {code:"es-PR", name:"Spanish (Puerto Rico)"},
        {code:"es-PY", name:"Spanish (Paraguay)"},
        {code:"es-SV", name:"Spanish (El Salvador)"},
        {code:"es-UY", name:"Spanish (Uruguay)"},
        {code:"es-VE", name:"Spanish (Venezuela)"},
        {code:"et", name:"Estonian"},
        {code:"et-EE", name:"Estonian (Estonia)"},
        {code:"eu", name:"Basque"},
        {code:"eu-ES", name:"Basque (Spain)"},
        {code:"fa", name:"Farsi"},
        {code:"fa-IR", name:"Farsi (Iran)"},
        {code:"fi", name:"Finnish"},
        {code:"fi-FI", name:"Finnish (Finland)"},
        {code:"fo", name:"Faroese"},
        {code:"fo-FO", name:"Faroese (Faroe Islands)"},
        {code:"fr", name:"French"},
        {code:"fr-BE", name:"French (Belgium)"},
        {code:"fr-CA", name:"French (Canada)"},
        {code:"fr-CH", name:"French (Switzerland)"},
        {code:"fr-FR", name:"French (France)"},
        {code:"fr-LU", name:"French (Luxembourg)"},
        {code:"fr-MC", name:"French (Principality of Monaco)"},
        {code:"gl", name:"Galician"},
        {code:"gl-ES", name:"Galician (Spain)"},
        {code:"gu", name:"Gujarati"},
        {code:"gu-IN", name:"Gujarati (India)"},
        {code:"he", name:"Hebrew"},
        {code:"he-IL", name:"Hebrew (Israel)"},
        {code:"hi", name:"Hindi"},
        {code:"hi-IN", name:"Hindi (India)"},
        {code:"hr", name:"Croatian"},
        {code:"hr-BA", name:"Croatian (Bosnia and Herzegovina)"},
        {code:"hr-HR", name:"Croatian (Croatia)"},
        {code:"hu", name:"Hungarian"},
        {code:"hu-HU", name:"Hungarian (Hungary)"},
        {code:"hy", name:"Armenian"},
        {code:"hy-AM", name:"Armenian (Armenia)"},
        {code:"id", name:"Indonesian"},
        {code:"id-ID", name:"Indonesian (Indonesia)"},
        {code:"is", name:"Icelandic"},
        {code:"is-IS", name:"Icelandic (Iceland)"},
        {code:"it", name:"Italian"},
        {code:"it-CH", name:"Italian (Switzerland)"},
        {code:"it-IT", name:"Italian (Italy)"},
        {code:"ja", name:"Japanese"},
        {code:"ja-JP", name:"Japanese (Japan)"},
        {code:"ka", name:"Georgian"},
        {code:"ka-GE", name:"Georgian (Georgia)"},
        {code:"kk", name:"Kazakh"},
        {code:"kk-KZ", name:"Kazakh (Kazakhstan)"},
        {code:"kn", name:"Kannada"},
        {code:"kn-IN", name:"Kannada (India)"},
        {code:"ko", name:"Korean"},
        {code:"ko-KR", name:"Korean (Korea)"},
        {code:"kok", name:"Konkani"},
        {code:"kok-IN", name:"Konkani (India)"},
        {code:"ky", name:"Kyrgyz"},
        {code:"ky-KG", name:"Kyrgyz (Kyrgyzstan)"},
        {code:"lt", name:"Lithuanian"},
        {code:"lt-LT", name:"Lithuanian (Lithuania)"},
        {code:"lv", name:"Latvian"},
        {code:"lv-LV", name:"Latvian (Latvia)"},
        {code:"mi", name:"Maori"},
        {code:"mi-NZ", name:"Maori (New Zealand)"},
        {code:"mk", name:"FYRO Macedonian"},
        {code:"mn", name:"Mongolian"},
        {code:"mn-MN", name:"Mongolian (Mongolia)"},
        {code:"mr", name:"Marathi"},
        {code:"mr-IN", name:"Marathi (India)"},
        {code:"ms", name:"Malay"},
        {code:"ms-BN", name:"Malay (Brunei Darussalam)"},
        {code:"ms-MY", name:"Malay (Malaysia)"},
        {code:"mt", name:"Maltese"},
        {code:"mt-MT", name:"Maltese (Malta)"},
        {code:"nb", name:"Norwegian (Bokm?l)"},
        {code:"nb-NO", name:"Norwegian (Bokm?l) (Norway)"},
        {code:"nl", name:"Dutch"},
        {code:"nl-BE", name:"Dutch (Belgium)"},
        {code:"nl-NL", name:"Dutch (Netherlands)"},
        {code:"nn-NO", name:"Norwegian (Nynorsk) (Norway)"},
        {code:"ns", name:"Northern Sotho"},
        {code:"ns-ZA", name:"Northern Sotho (South Africa)"},
        {code:"pa", name:"Punjabi"},
        {code:"pa-IN", name:"Punjabi (India)"},
        {code:"pl", name:"Polish"},
        {code:"pl-PL", name:"Polish (Poland)"},
        {code:"ps", name:"Pashto"},
        {code:"ps-AR", name:"Pashto (Afghanistan)"},
        {code:"pt", name:"Portuguese"},
        {code:"pt-BR", name:"Portuguese (Brazil)"},
        {code:"pt-PT", name:"Portuguese (Portugal)"},
        {code:"qu", name:"Quechua"},
        {code:"qu-BO", name:"Quechua (Bolivia)"},
        {code:"qu-EC", name:"Quechua (Ecuador)"},
        {code:"qu-PE", name:"Quechua (Peru)"},
        {code:"ro", name:"Romanian"},
        {code:"ro-RO", name:"Romanian (Romania)"},
        {code:"ru", name:"Russian"},
        {code:"ru-RU", name:"Russian (Russia)"},
        {code:"sa", name:"Sanskrit"},
        {code:"sa-IN", name:"Sanskrit (India)"},
        {code:"se", name:"Sami (Northern)"},
        {code:"se-FI", name:"Sami (Northern) (Finland)"},
        {code:"se-FI", name:"Sami (Skolt) (Finland)"},
        {code:"se-FI", name:"Sami (Inari) (Finland)"},
        {code:"se-NO", name:"Sami (Northern) (Norway)"},
        {code:"se-NO", name:"Sami (Lule) (Norway)"},
        {code:"se-NO", name:"Sami (Southern) (Norway)"},
        {code:"se-SE", name:"Sami (Northern) (Sweden)"},
        {code:"se-SE", name:"Sami (Lule) (Sweden)"},
        {code:"se-SE", name:"Sami (Southern) (Sweden)"},
        {code:"sk", name:"Slovak"},
        {code:"sk-SK", name:"Slovak (Slovakia)"},
        {code:"sl", name:"Slovenian"},
        {code:"sl-SI", name:"Slovenian (Slovenia)"},
        {code:"sq", name:"Albanian"},
        {code:"sq-AL", name:"Albanian (Albania)"},
        {code:"sr-BA", name:"Serbian (Latin) (Bosnia and Herzegovina)"},
        {code:"sr-BA", name:"Serbian (Cyrillic) (Bosnia and Herzegovina)"},
        {code:"sr-SP", name:"Serbian (Latin) (Serbia and Montenegro)"},
        {code:"sr-SP", name:"Serbian (Cyrillic) (Serbia and Montenegro)"},
        {code:"sv", name:"Swedish"},
        {code:"sv-FI", name:"Swedish (Finland)"},
        {code:"sv-SE", name:"Swedish (Sweden)"},
        {code:"sw", name:"Swahili"},
        {code:"sw-KE", name:"Swahili (Kenya)"},
        {code:"syr", name:"Syriac"},
        {code:"syr-SY", name:"Syriac (Syria)"},
        {code:"ta", name:"Tamil"},
        {code:"ta-IN", name:"Tamil (India)"},
        {code:"te", name:"Telugu"},
        {code:"te-IN", name:"Telugu (India)"},
        {code:"th", name:"Thai"},
        {code:"th-TH", name:"Thai (Thailand)"},
        {code:"tl", name:"Tagalog"},
        {code:"tl-PH", name:"Tagalog (Philippines)"},
        {code:"tn", name:"Tswana"},
        {code:"tn-ZA", name:"Tswana (South Africa)"},
        {code:"tr", name:"Turkish"},
        {code:"tr-TR", name:"Turkish (Turkey)"},
        {code:"tt", name:"Tatar"},
        {code:"tt-RU", name:"Tatar (Russia)"},
        {code:"ts", name:"Tsonga"},
        {code:"uk", name:"Ukrainian"},
        {code:"uk-UA", name:"Ukrainian (Ukraine)"},
        {code:"ur", name:"Urdu"},
        {code:"ur-PK", name:"Urdu (Islamic Republic of Pakistan)"},
        {code:"uz", name:"Uzbek (Latin)"},
        {code:"uz-UZ", name:"Uzbek (Latin) (Uzbekistan)"},
        {code:"uz-UZ", name:"Uzbek (Cyrillic) (Uzbekistan)"},
        {code:"vi", name:"Vietnamese"},
        {code:"vi-VN", name:"Vietnamese (Viet Nam)"},
        {code:"xh", name:"Xhosa"},
        {code:"xh-ZA", name:"Xhosa (South Africa)"},
        {code:"zh", name:"Chinese"},
        {code:"zh-CN", name:"Chinese (Simplified)"},
        {code:"zh-HK", name:"Chinese (Hong Kong)"},
        {code:"zh-MO", name:"Chinese (Macau)"},
        {code:"zh-SG", name:"Chinese (Singapore)"},
        {code:"zh-TW", name:"Chinese (Taiwan)"},
        {code:"zu", name:"Zulu"},
        {code:"zu-ZA", name:"Zulu (South Africa)"}
    ]);
    return langcodes;
}


export function getAccessStatusString(value) {
    if (value >= 100) return "freigeschaltet";

    switch(value) {
        case 0: return "inaktiv";
        case 1: return "gesperrt";
        case 2: return "gesperrt (abrechnungsproblem)";
        case 99: return "freigeschaltet (neues Passwort)";
        default: return "gesperrt ("+value.toString()+")";
    }
}

// Converts the given date object to an ISO timestamp without timezone
export function makeTimestamp(dt) { // dt: Date
    let s = dt?.toLocaleString('sv') ?? ''; // use sweden locale
    return s.replace(' ', 'T');
}

export function formatDateTime(value, locale = "de-DE") {
  if (value?.length > 0) {
    let t = timestampStringToDate(value);
    return t.toLocaleDateString(locale, dateoptions) + " " + t.toLocaleTimeString(locale, timeoptions);
  }
  return "";
}

export function formatDate(value, locale = "de-DE") {
  if (value?.length > 0) {
    let t = timestampStringToDate(value);
    return t.toLocaleDateString(locale, dateoptions);
  }
  return "";
}

export function formatFileSize(size) {
  if (Number.isFinite(size)) {
    let i = (0 == size ? 0 : Math.floor( Math.log(size) / Math.log(1024) ));
    return Number(( size / Math.pow(1024, i) ).toFixed(2)) * 1 + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
  }
  return '';
}

export function isDefinedAndNotNull(v) {
    const t = typeof v;
    if (t == "undefined" || t == null) {
        return false;
    }
    return true;
}

export function formatBundletype(bundletype) {
  switch (bundletype) {
    case 0 : return 'monolith';
    case 1 : return 't2m bundle';
    case 2 : return 't2m partitioned bundle';
  }
  return 'unknown';
}

export function formatUserRole(role) {
  switch (role) {
    case 1 : return 'Administrator';
    case 2 : return 'Account owner';
    case 0 :
    default:
        break;
  }
  return 'User';
}

export function formatUserAccessStatus(access_status, tpassresetexpiration, locale = "de-DE") {
  let t = null;

  if (tpassresetexpiration) {
    t = timestampStringToDate(tpassresetexpiration, true);
  }

  if (0 == access_status) {
    if (t) {
        if (t < today) { 
          return "No access. The user never set a password. Check user's e-mail.";
        } else {
          return "No access yet. The user must set a password until "+t.toLocaleDateString(locale, dateoptions) + " " + t.toLocaleTimeString(locale, timeoptions);
        }
    }
    return "No access";
  } else if (access_status < 99) {
    return "Access disabled";
  } else if (99 == access_status) {
    if (t) {
        if (t < today) { 
          return "No access. Password reset link expired";
        } else {
          return "No access. Created password reset link valid until "+t.toLocaleDateString(locale, dateoptions) + " " + t.toLocaleTimeString(locale, timeoptions);
        }
    }
    return "No access. Password reset.";
  }
  
  return 'Normal access';
}

export class UserAccessMask {
    static ReadFleet = 0x01;
    static ManageRegions = 0x02;
    static ManageGroups = 0x04;
    static ManagePlans  = 0x08;
    static ManageBundles = 0x10;
    static ManageDeviceAttributes = 0x20;
    static MoveDevicesAway  = 0x40;
    static ReadDeviceHistory = 0x80; // can read network settings, modes and the browser config including the history of checkins
    static WriteDeviceSetup = 0x100;
    static ReadShval = 0x200;
    static WriteShval = 0x400;
    static ReadLogstream = 0x800;
    static MoveDevicesInto  = 0x1000;
}

export function hasUserAccess(access_flags, mask) {
    if (access_flags) return ((access_flags & mask) != 0);
    else return false;
}

export class CloudFeatureMask {
    static SoftwareUpdates = 0x01;
    static SharedValues = 0x02;
    static LogStreams = 0x04;
    static DiagCodes = 0x08;
    static JobErrorMonitoring = 0x10;
    static RemoteJobExecution = 0x20;
    static LiveSupport = 0x40;
}

export function hasCloudFeatureEnabled(cloudfeatures, featuremask) {
    if (cloudfeatures) return ((cloudfeatures & featuremask) != 0);
    else return false;
}

export class DeviceModeMask {
    static OfflineOnly = 0x01;
    static VerboseMode = 0x02;
    static IgnoreSignatureCheck = 0x04;
    static ManualUpdatesOnly = 0x08;
    static DevMode = 0x10;
    static AllowUpdateFromMassStorage = 0x20;
}

export function isDevicemodeEnabled(enabledmodes, modemask) {
    if (enabledmodes) return ((enabledmodes & modemask) == modemask);
    else return false;
}

export function checkHostnameIsValid(s) {
    const re = /^[a-z\d]([a-z\d\\-]{0,61}[a-z\d])?(\.[a-z\d]([a-z\d\\-]{0,61}[a-z\d])?)*$/i;
    if (s.length > 0 && s.length < 64) {
      return re.test(s);
    }
    return false;
  }
  
